
    // IMPORTS
    import { computed, defineComponent, onBeforeUnmount, onMounted, PropType, reactive, ref, watchEffect } from 'vue'
    import router from '@/router'
    import { useEquipmentStore } from '@/store/equipment'
    import { useSparePartsStore } from '@/store/spareParts'
    import { useSparePartFilterStore } from '@/store/sparePartFilter'
    import { useClassificationStore } from '@/store/classification'
    import { useUserStore } from '@/store/user'
    import { buildClassificationLocalization, buildLocalization, buildMaterialLocalization } from '@/util'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-vue-layout'
    import { ExpansionPanel, ExpansionPanelContent } from '@progress/kendo-vue-layout'
    import { Slider } from '@progress/kendo-vue-inputs'
    import { Button } from '@progress/kendo-vue-buttons'
    import { Push, Reveal } from '@progress/kendo-vue-animation'
    import SparepartTypeTree from './sparepart-type-tree.vue'
    import SparepartSearchFilter from './sparepart-search-filter.vue'
    import SparepartFunctionFilter from './sparepart-function-filter.vue'
    import SparepartFilterState from './sparepart-filter-state.vue'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import MyIcon from '@/components/common/my-icon.vue'
    import MaterialLink from '@/components/common/material-link.vue'
    import DynamicViewSwitcher from '@/components/common/dynamic-view-switcher.vue'
    import ScrollArrows from '@/components/common/scroll-arrows.vue'

    import { IMaterialDTO } from '@/types/material'
    import { DbConstants } from '@/const'

    interface IMaterialFilterState {
        selectedTypes: string[],
        selectedFunctions: string[],
        searchFunction: ((material: IMaterialDTO) => boolean) | null
    }

    export default defineComponent({
        name: 'sparepartView',
        components: {
            LoaderGuard,
            Splitter,
            ExpansionPanel,
            ExpansionPanelContent,
            SparepartTypeTree,
            SparepartFunctionFilter,
            SparepartSearchFilter,
            SparepartFilterState,
            MaterialLink,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            MyIcon,
            'k-button': Button,
            Push,
            Reveal,
            DynamicViewSwitcher,
            Slider,
            ScrollArrows
        },

        props: {
            modelFilter: { type: String as PropType<string>, required: true },
            equipmentMode: { type: Boolean as PropType<boolean>, required: false, default: () => false },
        },

        setup(props) {
            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const sparepartStore = useSparePartsStore()
            const equipmentStore = useEquipmentStore()
            const classificationStore = useClassificationStore()
            const sparePartFilterStore = useSparePartFilterStore()

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const cellSizeSmallMin = 1
            const cellSizeSmallMax = 4

            const splitterPanes = ref<SplitterPaneProps[]>([
                { size: '350px', content: 'tree', resizable: true, containsSplitter: false, min: '200px', max: '500px' },
                { content: 'main', resizable: true, containsSplitter: false }
            ])

            const filterState = reactive({
                selectedTypes: [],
                selectedFunctions: [],
                searchFunction: null
            } as IMaterialFilterState)

            const smallScreenState = reactive({
                typeFilterActive: false,
                functionFilterActive: false,
                textFilterActive: false
            })

            const typeFilterExpanded = ref(true)
            const functionFilterExpanded = ref(true)

            const cellSize = ref(200)
            const cellSizeSmall = ref(2)
            const resultCardRef = ref<InstanceType<typeof Card> | null>(null)
            const mobileResultRef = ref<HTMLElement | null>(null)

            const publicData = {
                splitterPanes,
                filterState,
                smallScreenState,
                typeFilterExpanded,
                functionFilterExpanded,
                cellSize,
                cellSizeSmall,
                cellSizeSmallMin,
                cellSizeSmallMax,
                resultCardRef,
                mobileResultRef
            }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const showTypeFilter = computed(() => smallScreenState.typeFilterActive)
            const showFunctionFilter = computed(() => smallScreenState.functionFilterActive)
            const showTextFilter = computed(() => smallScreenState.textFilterActive)
            const showResultGrid = computed(() => !showTypeFilter.value && !showFunctionFilter.value && !showTextFilter.value)

            const gridState = computed(() => sparePartFilterStore.gridState)
            const numberOfMaterialsToDisplay = computed(() => gridState.value.numberOfLoadedParts)

            const functionFilterRoot = computed(() => getRootModel(props.modelFilter))

            const dataSource = computed(() => props.equipmentMode
                ? equipmentStore.bomData
                : props.modelFilter === 'all'
                    ? sparepartStore.spareParts
                    : sparepartStore.spareParts?.filter(m => m.material.modelsContainingSparePart.includes(props.modelFilter))
            )

            /** Indicates whether spare part data is not generally available for the equipment yet */
            const dataNotAvailableYet = computed(() => props.equipmentMode && equipmentStore.bomData?.length === 0)

            const hasLoaded = computed(() => dataSource.value != null)

            const filteredMaterials = computed(() => sparePartFilterStore.filteredSpareParts)
            const fileredMaterialsCount = computed(() => filteredMaterials.value.length)
            const materialsToDisplay = computed(() => filteredMaterials.value.slice(0, numberOfMaterialsToDisplay.value))

            const resultTitle = computed(() => getResultTitle(fileredMaterialsCount.value))
            const displayingXoutOfY = computed(() => getLabelXoutOfY(numberOfMaterialsToDisplay.value, fileredMaterialsCount.value))

            const showLoadMore = computed(() => numberOfMaterialsToDisplay.value < fileredMaterialsCount.value)

            const typeFilterPanelTitle = computed(() => getTypeFilterPanelTitle())
            const functionFilterPanelTitle = computed(() => getFunctionFilterPanelTitle())
            const filterIsActive = computed(() => sparePartFilterStore.filterIsActive)

            const computedData = {
                functionFilterRoot,
                hasLoaded,
                dataNotAvailableYet,
                filteredMaterials,
                fileredMaterialsCount,
                materialsToDisplay,
                resultTitle,
                displayingXoutOfY,
                showLoadMore,
                showTypeFilter,
                showFunctionFilter,
                showTextFilter,
                showResultGrid,
                typeFilterPanelTitle,
                functionFilterPanelTitle,
                filterIsActive
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()
            const { materialTextFor } = buildMaterialLocalization()
            const { localizePropValue } = buildClassificationLocalization()

            function onSplitterResize(event: SplitterOnChangeEvent) {
                splitterPanes.value = event.newState
            }

            function searchResultTitle(material: IMaterialDTO): string {
                return `${material?.materialNo} ${materialTextFor(material)}`
            }

            function onLoadMore() {
                gridState.value.numberOfLoadedParts += 50
            }

            function redirectToMaterialView(material: IMaterialDTO) {
                if (material.materialNo) {
                    // Save state
                    gridState.value.selectedId = getId(material)

                    // Redirect
                    router.push({ name: 'material', params: { matno: material.materialNo } })
                }
            }

            function getId(material: IMaterialDTO): string {
                return `sp${material.materialNo}`
            }

            function onToggleTypeFilter() {
                smallScreenState.typeFilterActive = !smallScreenState.typeFilterActive
                smallScreenState.functionFilterActive = false
                smallScreenState.textFilterActive = false
            }

            function onToggleFunctionFilter() {
                smallScreenState.typeFilterActive = false
                smallScreenState.functionFilterActive = !smallScreenState.functionFilterActive
                smallScreenState.textFilterActive = false
            }

            function onToggleTextFilter() {
                smallScreenState.typeFilterActive = false
                smallScreenState.functionFilterActive = false
                smallScreenState.textFilterActive = !smallScreenState.textFilterActive
            }

            function onSearchFilterChanged() {
                onToggleTextFilter() // auto hide text filter on search
            }

            function onTypeFilterPanelAction() {
                typeFilterExpanded.value = !typeFilterExpanded.value
            }

            function onFunctionFilterPanelAction() {
                functionFilterExpanded.value = !functionFilterExpanded.value
            }

            function zoom(delta: number) {
                const newSize = Math.min(cellSizeSmallMax, Math.max(cellSizeSmallMin, cellSizeSmall.value + delta))
                cellSizeSmall.value = newSize
            }

            function onResetFilters() {
                sparePartFilterStore.resetFilterState()
            }

            const publicMethods = {
                localize,
                materialTextFor,
                onSplitterResize,
                searchResultTitle,
                onLoadMore,
                redirectToMaterialView,
                getId,
                onToggleTypeFilter,
                onToggleFunctionFilter,
                onToggleTextFilter,
                onSearchFilterChanged,
                onTypeFilterPanelAction,
                onFunctionFilterPanelAction,
                zoom,
                onResetFilters
            }
            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function getRootModel(propValue: string): string {
                if (propValue === 'all') return ''

                const pvHierarchy = classificationStore.getHierarchy(DbConstants.sparePartPropertyNames.sparePartModel, propValue)

                const result = pvHierarchy.length
                    ? pvHierarchy[0].propertyValue
                    : 'all'

                return result
            }

            function getResultTitle(totalCount: number): string {
                return hasLoaded.value
                    ? `${localize('sparePartsFound')}: ${totalCount}`
                    : localize('loading')
            }

            function getLabelXoutOfY(displayCount: number, totalCount: number): string {
                return localize('DisplayingXoutOfYSpareParts')
                    .replaceAll('{0}', Math.min(displayCount, totalCount).toString())
                    .replaceAll('{1}', totalCount.toString())
            }

            function getTypeFilterPanelTitle(): string {
                const selectedTypeId = sparePartFilterStore.typeTreeState.selected.ids[0]
                const baseLabel = localize('FilterByType')

                if (!selectedTypeId || selectedTypeId === 'all') return baseLabel

                const typeName = localizePropValue(DbConstants.sparePartPropertyNames.sparePartType, selectedTypeId)
                return `${baseLabel} · ${typeName}`
            }

            function getFunctionFilterPanelTitle(): string {
                const count = sparePartFilterStore.selectedFunctions.length
                const baseLabel = localize('FilterByFunction')

                return count > 0 ? `${baseLabel} (${count})` : baseLabel
            }

            function tryToScroll(id: string, attemptCount: number) {
                const target = document.getElementById(id)

                if (target == null) {
                    // item not rendered yet, make new attempt later

                    if (attemptCount > 50) return // give up
                    setTimeout(() => tryToScroll(id, attemptCount + 1), 100)
                    return
                }

                target.scrollIntoView({ block: 'center', behavior: 'auto' })
                target.classList.add('active')
            }

            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            // Initialize spare part filter store
            watchEffect(() => {
                const spareParts = dataSource.value?.map(bom => bom.material) ?? null
                sparePartFilterStore.setSpareParts(spareParts)
            })

            // Scroll to item according to previously stored state?
            if (gridState.value.selectedId != null) {
                tryToScroll(gridState.value.selectedId, 1)
            }

            const userStore = useUserStore()

            onMounted(() => {
                const cellSizes = userStore.getGridCellSize()
                cellSize.value = cellSizes.cellSize
                cellSizeSmall.value = cellSizes.cellSizeSmall
            })

            onBeforeUnmount(() => {
                userStore.setGridCellSize({
                    cellSize: cellSize.value,
                    cellSizeSmall: cellSizeSmall.value

                })
            })

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
